import type { PaymentIconTypes } from '@/modules/perseo-billing/containers/payment-complete/components/purchase-payment-methods';
import type { IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';

import type {
  TBillingOrderConfirmationProductItem,
  TOrderConfirmationDelivery
} from './summary.interface';

export interface IOrder {
  orderId: string;
  createdBy: string;
  paymentStatus: string;
  documentType: string;
  creationDate: string;
  lastModified: string;
  confirmationStatus: string;
  channelType: string;
  orderType: string;
  status: string;
  currency: string;
  ack: boolean;
  channelDetails: IChannelDetails;
  pricesDetails: IPricesDetails;
  clientDetails: IClientDetails;
  items: IItem[];
  billingAddress?: IBillingAddress;
  payments: IPayment[];
  customerInfo: ICustomerInfo;
  shipments: IShipment[];
  commerceCodeDetails: ICommerceCodeDetail[];
  orderPriceAdjustments: IOrderPriceAdjustment[];
  couponItems: ICouponItem[];
  couponTotal: string;
  customAttributes: ICustomAttribute[];
  authType: string;
  isCencopay: boolean;
  cencoPayStatus?: EPayStatus;
  wasPaidWithCencoPayBalance?: boolean;
}

export interface IChannelDetails {
  siteId: string;
  companyName: string;
  country: string;
  device: string;
  channel: string;
  companyCode: string;
}

export interface IPricesDetails {
  shippingTotal: number;
  totalNetPrice: number;
  orderTotal: number;
  productSubTotal: number;
  cencosudOrderTotal: number;
  totalTax: number;
}

export interface IClientDetails {
  clientIp: string;
  appliance: string;
  userAgent: string;
  device: string;
}

export interface IItem {
  adjustment?: IItemAdjustment[];
  arrivalDate: string;
  arrivalDateEnd?: string;
  attributes?: IAttribute[];
  basePrice: number;
  bonusProductLineItem: boolean;
  branchId: string;
  brandName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bundledProducts?: any;
  cMediaLineItem?: string;
  cencosudPrice?: number;
  classId: string;
  className: string;
  customAttributes: ICustomAttribute[];
  deliveryDate: string;
  deliveryFinalRangeDate?: string;
  deliverySlot?: string;
  deliveryTime: string;
  deliveryTimeLabel: string;
  deliveryType?: string;
  departamentJDA: string;
  departmentId: string;
  departmentName: string;
  dispatchDate: string;
  EAN: string;
  extraSellerInfo: string;
  facility: string;
  gift: boolean;
  giftcard: boolean;
  grossPrice: number;
  grossPriceGiftcard?: number;
  intangible: boolean;
  intgDeliveryMethod?: string;
  isColabDiscount?: boolean;
  isMarketplace: boolean;
  isPreventa?: boolean;
  isSecondHand?: boolean;
  itemText: string;
  itemId: string;
  manufacturerSKU?: string;
  masterId?: string;
  netPrice: number;
  opaqueUserId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
  originShipmentId: string;
  originType?: string;
  price: number;
  priceAfterDiscounts: number;
  priceAfterItemDiscount: number;
  priceAfterOrderDiscount: number;
  primaryCategoryId?: string;
  productId: string;
  productName: string;
  quantity: number;
  referencePrice?: number;
  reviewsCount?: number;
  sellerId: string;
  sellerSku: string;
  shipmentCategory: string;
  shipmentId: string;
  shippingType: string;
  size?: string;
  sku: string;
  slotId: string;
  slotType: string;
  storeId?: string;
  subdepartmentId: string;
  subdepartmentName: string;
  tax: number;
  taxBasis: number;
  taxClassId: string;
  taxRate: number;
  title: string;
  type: string;
  urlImg: string;
  vendorId: string;
  workingDays: string;
  workingDaysRange: string;
}

export interface IItemAdjustment {
  label?: string;
  amount?: number;
}

export interface IAttribute {
  attributeId: string;
  displayValue: string;
  code: string;
}
export interface IBillingAddress {
  stateCode?: string;
  latitude?: string;
  longitude?: string;
  plusCode?: string;
  isVerified?: boolean;
  id: string;
  firstName: string;
  lastName: string;
  type: string;
  country: string;
  region: string;
  city: string;
  cityCode: string;
  address1: string;
  address2: string;
  address3: string;
  phone: string;
  postalCode: string;
  regionCode: string;
  customAttributes: ICustomAttribute[];
}

export type TPaymentTypeCode = 'CREDIT_CARD' | 'DEBIT_CARD' | 'PRE_PAID';

export interface IPayment {
  id: string;
  processorId?: string;
  paymentInstrumentId: string;
  amount: number;
  kind: string;
  status: string;
  currencyCode?: string;
  paymentMethod?: IPaymentMethodProcessor;
  paymentInfo?: ICustomAttribute[];
  paymentCard?: IPaymentCard;
  customAttributes?: ICustomAttribute[];
  paymentTypeCode?: TPaymentTypeCode;
}

export interface IPaymentMethodProcessor {
  id: string;
  processor: string;
}

export type TCardVendorName = 'Visa' | 'MasterCard' | 'Amex';

export interface IPaymentCard {
  cardType?: TCardVendorName;
  creditCardExpired?: boolean;
  maskedNumber?: string;
  numberLastDigits?: string;
}

export interface ICustomAttribute {
  label: string;
  value: string;
}

export interface ICustomerInfo {
  // streetAddress?: string;
  // city?: string;
  // region?: string;
  // postCode?: string;
  customerName?: string;
  customerId: string;
  isAuthenticated: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  identityNocumentNumber: string;
  identityDocumentType: string;
  points: boolean;
  customAttributes: ICustomAttribute[];
}

export interface IShipment {
  id: string;
  shippingCost: number;
  number: number;
  address?: IAddress;
  status: string;
  shippingMethod: IShippingMethod;
  shipingCostRecalculated: IShipingCostRecalculated;
  products: string[];
  adjustments: IAdjustment[];
  customAttributes: ICustomAttribute2[];
  collectOnBehalfName?: string;
  pickupStoreName?: string;
  pickupStoreStreetName?: string;
  pickupStoreStreetNumber?: string;
  pickupStoreCommune?: string;
}

export interface IAddress {
  plusCode: string;
  additionalInformation: string;
  firstName: string;
  lastName: string;
  type: string;
  country: string;
  region: string;
  regionCode: string;
  city: string;
  cityCode: string;
  address1: string;
  address2: string;
  address3: string;
  phone: string;
  latitude: string;
  longitude: string;
  isVerified: boolean;
  postalCode: string;
  moreInfo?: string;
}

export interface IShippingMethod {
  shippingMethodId: string;
  name: string;
  type: string;
  isScheduled: boolean;
}

export interface IShipingCostRecalculated {
  cost: number;
  discount: number;
}

export interface IAdjustment {
  label: string;
  amount: number;
}

export interface ICustomAttribute2 {
  label: string;
  value: string;
}

export interface ICommerceCodeDetail {
  commerceCodeDetailsId: string;
  commerceCode: string;
  products: string[];
  originPaymentIds: string[];
  originOrderNumber: string;
  originShipmentIds: string[];
  commerceCostRecalculated: ICommerceCostRecalculated;
}

export interface ICommerceCostRecalculated {
  totalProducts: number;
  totalShippments: number;
  totalDiscounts: number;
  totalGrossPrice: number;
}

export interface IOrderPriceAdjustment {
  appliedDiscount: IAppliedDiscount;
  couponCode: string;
  creationDate: string;
  custom: boolean;
  itemText: string;
  lastModified: string;
  manual: boolean;
  price: number;
  priceAdjustmentId: string;
  promotionId: string;
}

export interface IAppliedDiscount {
  amount: number;
  percentage: number;
  type: string;
}

export interface ICouponItem {
  code: string;
  couponItemId: string;
  statusCode: string;
  valid: boolean;
  amount?: number;
  couponType?: string;
}

interface IBasketSummary {
  soldBy?: string;
  sendBy?: string;
  deliveryDetails: TOrderConfirmationDelivery;
  items: TBillingOrderConfirmationProductItem[];
  timeStamp: string;
}

interface IPaymentMethod {
  type: string;
  cardNumber?: string;
  cardName?: string;
  cardBankName?: string;
  bankCardType?: string;
  amount: string;
  cardVendorName?: PaymentIconTypes;
  paymentTransactionType?: string;
}

interface ISummary {
  isCencoPrice: boolean;
  isCencoPayPrice?: boolean;
  subTotalPrice: string;
  totalDeliveryPrice: string;
  totalCouponPrice: string;
  totalPriceGiftCard: string;
  totalPrice: string;
  unformattedTotalPrice: number;
  currency: string;
  totalTax: number;
  wasPaidWithCencoPayBalance?: boolean;
  wasPaidOnlyWithGiftCard?: boolean;
}

interface ITotalSummary {
  paymentMethod: IPaymentMethod[];
  summary: ISummary;
}

interface ICustomer {
  isBill: boolean;
  customerName: string;
  email: string;
  rut: string;
  phone: string;
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  region: string;
  postCode: string;
  country: string;
  documentType: IdentificationDocumentType;
  documentID: string;
}

enum EPayStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  IN_PROGRESS = 'IN_PROGRESS'
}

interface IOrderConfirmation {
  id: string;
  order: string;
  authType?: string;
  isCencoPay?: boolean;
  cencoPayStatus?: EPayStatus;
  basketSummary: IBasketSummary[];
  totalSummary: ITotalSummary;
  customer: ICustomer;
  gtm?: IGtmOrderConfirmation;
  url?: string; // used in webPay
  status?: string;
  tokenUserId?: string;
  isFastCheckout?: boolean;
  customerId?: string;
  anonymousId?: string;
}

export interface IOrderIntentResponse {
  orderNumber: string;
  qrCode: string;
  redirectURI: string;
  simpleCode: string;
  transactionCountdown: number;
}

interface IGtmOrderConfirmation {
  tax: number;
  shipping: number;
  coupon?: string;
}

export { EPayStatus };
export type {
  IBasketSummary,
  ICustomer,
  IOrderConfirmation,
  IPaymentMethod,
  ITotalSummary
};
